// Reads client data from the text of two digital PDFs issued by the
// government: the SAT's Constancia de Situación Fiscal (CSF) and RENAPO's
// constancia de la CURP. Scans and photos have no text layer and are not read:
// there is no OCR, so no document ever leaves the server to be read.
//
// The API extracts the positioned text items of the PDF (pdf.js); this module
// rebuilds the label → value pairs from their positions. In the CSF every
// label and its value are separate items on the same line, laid out in two
// columns, and a long value wraps onto the next line of its column:
//
//   Código Postal:   44100              Tipo de Vialidad:    AVENIDA (AV.)
//   Número Interior:                    Nombre de la Colonia: UNIVERSIDAD NACIONAL AUTONOMA DE
//                                       MEXICO C U
import {
  CURP_IN_TEXT,
  curpCheckDigitMatches,
  dateFromRfc,
  decodeCurp,
  isValidCurp,
  RFC_PATTERN,
  rfcPersonType,
} from "./curp";
import { formatIsoDate, isValidDate, today } from "./dates";
import type { DocumentExtraction, ExtractedClientFields, IsoDate } from "./types";

/** A piece of text on a PDF page; `x` and `y` in points from the page's bottom-left corner. */
export interface PdfTextItem {
  text: string;
  x: number;
  y: number;
  page: number;
}

/** Labels of both documents, in normalized form (no accents, upper case, no colon). */
const LABELS = {
  // Constancia de Situación Fiscal
  rfc: ["RFC"],
  curp: ["CURP"],
  givenNames: ["NOMBRE (S)", "NOMBRE(S)", "NOMBRES"],
  firstSurname: ["PRIMER APELLIDO"],
  secondSurname: ["SEGUNDO APELLIDO"],
  legalName: ["DENOMINACION/RAZON SOCIAL", "DENOMINACION / RAZON SOCIAL", "DENOMINACION O RAZON SOCIAL"],
  capitalRegime: ["REGIMEN CAPITAL"],
  tradeName: ["NOMBRE COMERCIAL"],
  operationsStart: ["FECHA INICIO DE OPERACIONES"],
  taxStatus: ["ESTATUS EN EL PADRON"],
  taxStatusDate: ["FECHA DE ULTIMO CAMBIO DE ESTADO"],
  postalCode: ["CODIGO POSTAL"],
  roadType: ["TIPO DE VIALIDAD"],
  roadName: ["NOMBRE DE VIALIDAD"],
  exteriorNumber: ["NUMERO EXTERIOR"],
  interiorNumber: ["NUMERO INTERIOR"],
  neighborhood: ["NOMBRE DE LA COLONIA"],
  locality: ["NOMBRE DE LA LOCALIDAD"],
  municipality: ["NOMBRE DEL MUNICIPIO O DEMARCACION TERRITORIAL"],
  state: ["NOMBRE DE LA ENTIDAD FEDERATIVA"],
  betweenStreet: ["ENTRE CALLE"],
  andStreet: ["Y CALLE"],
  email: ["CORREO ELECTRONICO"],
  landlineAreaCode: ["TEL. FIJO LADA", "TEL FIJO LADA"],
  mobileAreaCode: ["TEL. MOVIL LADA", "TEL MOVIL LADA"],
  phoneNumber: ["NUMERO"],
  addressStatus: ["ESTADO DEL DOMICILIO"],
  taxpayerAddressStatus: ["ESTADO DEL CONTRIBUYENTE EN EL DOMICILIO"],
  originalString: ["CADENA ORIGINAL SELLO"],
  digitalSeal: ["SELLO DIGITAL"],
  // Constancia de la CURP
  key: ["CLAVE"],
  fullName: ["NOMBRE"],
  birthDate: ["FECHA DE NACIMIENTO"],
  sex: ["SEXO"],
  nationality: ["NACIONALIDAD"],
  birthState: ["ENTIDAD DE NACIMIENTO"],
  registrationDate: ["FECHA DE INSCRIPCION"],
  folio: ["FOLIO"],
  registrationState: ["ENTIDAD DE REGISTRO"],
} as const;

type LabelKey = keyof typeof LABELS;

const normalize = (text: string) =>
  text
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toUpperCase()
    .replace(/\s+/g, " ")
    .trim();

const LABEL_BY_TEXT = new Map<string, LabelKey>(
  (Object.entries(LABELS) as [LabelKey, readonly string[]][]).flatMap(([key, texts]) =>
    texts.map((text) => [text, key] as const),
  ),
);

/** Items closer than this vertically are on the same line. */
const LINE = 2.5;
/** Labels closer than this horizontally start the same column. */
const COLUMN = 12;
/** How far below its label a value may wrap when no other label follows in its column. */
const WRAP = 18;

interface Label {
  key: LabelKey;
  /** Value written in the same item, after the colon ("Código Postal:06000"). */
  inline: string;
  item: PdfTextItem;
}

function asLabel(item: PdfTextItem): Label | null {
  const raw = item.text.trim();
  const colon = raw.indexOf(":");
  const head = normalize(colon >= 0 ? raw.slice(0, colon) : raw);
  const key = LABEL_BY_TEXT.get(head);
  return key ? { key, inline: colon >= 0 ? raw.slice(colon + 1).trim() : "", item } : null;
}

/** Left edges of the label columns of a page. */
function columnStarts(xs: readonly number[]): number[] {
  const starts: number[] = [];
  for (const x of [...xs].sort((a, b) => a - b)) {
    const last = starts.at(-1);
    if (last === undefined || x - last > COLUMN) starts.push(x);
  }
  return starts;
}

/** Joins items in reading order: top to bottom, then left to right within a line. */
function readingOrder(items: readonly PdfTextItem[]): string {
  const lines: PdfTextItem[][] = [];
  for (const item of [...items].sort((a, b) => b.y - a.y)) {
    const line = lines.at(-1);
    if (line?.[0] && line[0].y - item.y <= LINE) line.push(item);
    else lines.push([item]);
  }
  return lines
    .flatMap((line) => line.sort((a, b) => a.x - b.x).map((item) => item.text.trim()))
    .filter(Boolean)
    .join(" ");
}

export interface LabeledField {
  key: LabelKey;
  value: string;
  x: number;
  y: number;
  page: number;
}

/**
 * Every known label of the document with the text that belongs to it: what
 * follows it on its line (up to the next label or column) plus what wraps
 * below it in its column, up to the next label of that column.
 */
export function readLabeledFields(items: readonly PdfTextItem[]): LabeledField[] {
  const fields: LabeledField[] = [];
  const pages = [...new Set(items.map((item) => item.page))].sort((a, b) => a - b);
  for (const page of pages) {
    const labels: Label[] = [];
    const values: PdfTextItem[] = [];
    for (const item of items) {
      if (item.page !== page || item.text.trim() === "") continue;
      const label = asLabel(item);
      if (label) labels.push(label);
      else values.push(item);
    }
    const columns = columnStarts(labels.map((l) => l.item.x));
    // Reading order, so the first match of a key is the topmost one.
    labels.sort((a, b) => b.item.y - a.item.y || a.item.x - b.item.x);

    for (const label of labels) {
      const { x, y } = label.item;
      const nextColumn = columns.find((start) => start > x + COLUMN) ?? Number.POSITIVE_INFINITY;
      const nextOnLine = Math.min(
        ...labels.filter((l) => Math.abs(l.item.y - y) <= LINE && l.item.x > x + 1).map((l) => l.item.x),
      );
      const right = Math.min(nextColumn, nextOnLine);
      const labelsBelow = labels
        .filter((l) => Math.abs(l.item.x - x) <= COLUMN && l.item.y < y - LINE)
        .map((l) => l.item.y);
      const bottom = labelsBelow.length > 0 ? Math.max(...labelsBelow) + LINE : y - WRAP;
      const parts = values.filter((v) => v.x >= x - LINE && v.x < right && v.y <= y + LINE && v.y > bottom);
      const value = [label.inline, readingOrder(parts)].filter(Boolean).join(" ").replace(/\s+/g, " ").trim();
      fields.push({ key: label.key, value, x, y, page });
    }
  }
  return fields;
}

const firstValue = (fields: readonly LabeledField[], key: LabelKey) =>
  fields.find((f) => f.key === key && f.value !== "")?.value;

const cleanKey = (value: string | undefined) => (value ? normalize(value).replace(/[\s-]/g, "") : undefined);

const validRfc = (value: string | undefined) => {
  const rfc = cleanKey(value);
  return rfc && RFC_PATTERN.test(rfc) ? rfc : undefined;
};

const validCurp = (value: string | undefined) => {
  const curp = cleanKey(value);
  return curp && isValidCurp(curp) ? curp : undefined;
};

/** Abbreviations of the capital regimes the SAT prints apart from the legal name. */
const CAPITAL_REGIME_ABBREVIATIONS: Record<string, string> = {
  "SOCIEDAD ANONIMA": "S.A.",
  "SOCIEDAD ANONIMA DE CAPITAL VARIABLE": "S.A. DE C.V.",
  "SOCIEDAD ANONIMA BURSATIL": "S.A.B.",
  "SOCIEDAD ANONIMA BURSATIL DE CAPITAL VARIABLE": "S.A.B. DE C.V.",
  "SOCIEDAD ANONIMA PROMOTORA DE INVERSION": "S.A.P.I.",
  "SOCIEDAD ANONIMA PROMOTORA DE INVERSION DE CAPITAL VARIABLE": "S.A.P.I. DE C.V.",
  "SOCIEDAD DE RESPONSABILIDAD LIMITADA": "S. DE R.L.",
  "SOCIEDAD DE RESPONSABILIDAD LIMITADA DE CAPITAL VARIABLE": "S. DE R.L. DE C.V.",
  "SOCIEDAD POR ACCIONES SIMPLIFICADA": "S.A.S.",
  "SOCIEDAD POR ACCIONES SIMPLIFICADA DE CAPITAL VARIABLE": "S.A.S. DE C.V.",
  "SOCIEDAD CIVIL": "S.C.",
  "ASOCIACION CIVIL": "A.C.",
  "SOCIEDAD EN NOMBRE COLECTIVO": "S. EN N.C.",
  "SOCIEDAD EN COMANDITA SIMPLE": "S. EN C.S.",
  "SOCIEDAD COOPERATIVA DE RESPONSABILIDAD LIMITADA": "S.C. DE R.L.",
};

function legalEntityName(fields: readonly LabeledField[]): string | undefined {
  const name = firstValue(fields, "legalName");
  if (!name) return undefined;
  const regime = normalize(firstValue(fields, "capitalRegime") ?? "");
  if (!regime || regime === "SIN TIPO DE SOCIEDAD") return name;
  return `${name} ${CAPITAL_REGIME_ABBREVIATIONS[regime] ?? regime}`;
}

/** "AVENIDA JUAREZ 976 INT. 2, COL. CENTRO, C.P. 44100, GUADALAJARA, JALISCO". */
function taxAddress(fields: readonly LabeledField[]): string | undefined {
  const get = (key: LabelKey) => firstValue(fields, key);
  // "AVENIDA (AV.)" → "AVENIDA"; skipped when the road name already starts with it.
  const roadType = get("roadType")
    ?.replace(/\s*\(.*?\)\s*/g, " ")
    .trim();
  const roadName = get("roadName");
  const road =
    roadName && roadType && !normalize(roadName).startsWith(normalize(roadType)) ? `${roadType} ${roadName}` : roadName;
  const interior = get("interiorNumber");
  const street = [road, get("exteriorNumber"), interior && `INT. ${interior}`].filter(Boolean).join(" ");
  const neighborhood = get("neighborhood");
  const postalCode = get("postalCode")?.replace(/\D/g, "");
  const parts = [
    street,
    neighborhood && `COL. ${neighborhood}`,
    postalCode && `C.P. ${postalCode}`,
    get("municipality") ?? get("locality"),
    get("state"),
  ].filter(Boolean);
  // Without a street the rest is not a usable address.
  return street ? parts.join(", ") : undefined;
}

const SPANISH_MONTHS = [
  "ENERO",
  "FEBRERO",
  "MARZO",
  "ABRIL",
  "MAYO",
  "JUNIO",
  "JULIO",
  "AGOSTO",
  "SEPTIEMBRE",
  "OCTUBRE",
  "NOVIEMBRE",
  "DICIEMBRE",
];

/** "07 DE SEPTIEMBRE DE 1925" → "1925-09-07". */
function spanishDate(value: string | undefined): IsoDate | undefined {
  const match = /^(\d{1,2}) DE ([A-Z]+) DE (\d{4})$/.exec(normalize(value ?? "").replace("SETIEMBRE", "SEPTIEMBRE"));
  const month = match ? SPANISH_MONTHS.indexOf(match[2] as string) + 1 : 0;
  if (!match || month === 0) return undefined;
  const date = formatIsoDate(Number(match[3]), month, Number(match[1]));
  return isValidDate(date) ? date : undefined;
}

/** Area code + number of the first phone listed (older CSFs include them). */
function phone(fields: readonly LabeledField[]): string | undefined {
  for (const areaCode of fields.filter((f) => f.key === "landlineAreaCode" || f.key === "mobileAreaCode")) {
    const number = fields.find(
      (f) =>
        f.key === "phoneNumber" && f.page === areaCode.page && Math.abs(f.y - areaCode.y) <= LINE && f.x > areaCode.x,
    );
    const digits = number?.value.replace(/\D/g, "");
    if (digits) return [areaCode.value.replace(/\D/g, ""), digits].filter(Boolean).join(" ");
  }
  return undefined;
}

function fromTaxCertificate(fields: readonly LabeledField[], currentDate: IsoDate): ExtractedClientFields {
  const rfc = validRfc(firstValue(fields, "rfc"));
  const curp = validCurp(firstValue(fields, "curp"));
  const isLegalEntity =
    fields.some((f) => f.key === "legalName" || f.key === "capitalRegime") ||
    (!!rfc && rfcPersonType(rfc) === "legal_entity");
  const result: ExtractedClientFields = {
    type: isLegalEntity ? "legal_entity" : "individual",
    rfc,
    address: taxAddress(fields),
    phone: phone(fields),
    // The SAT registers a Mexican tax address.
    country: "México",
  };
  if (isLegalEntity) {
    result.name = legalEntityName(fields);
    // A legal entity's RFC carries its incorporation date. Its two-digit year is placed
    // in the century that keeps it on or before the start of operations ("25" → 1925, not
    // 2025, for an entity operating since 1925).
    const operationsStart = spanishDate(firstValue(fields, "operationsStart"));
    result.incorporationDate = rfc ? (dateFromRfc(rfc, operationsStart ?? currentDate) ?? undefined) : undefined;
  } else {
    const name = [
      firstValue(fields, "givenNames"),
      firstValue(fields, "firstSurname"),
      firstValue(fields, "secondSurname"),
    ]
      .filter(Boolean)
      .join(" ");
    result.name = name || undefined;
    result.curp = curp;
    result.birthDate = curp
      ? decodeCurp(curp)?.birthDate
      : rfc
        ? (dateFromRfc(rfc, currentDate) ?? undefined)
        : undefined;
  }
  return result;
}

/** Initials of a full name must include the paternal surname and the given name of the CURP. */
function nameMatchesCurp(name: string, curp: string): boolean {
  const initials = new Set(
    normalize(name)
      .split(" ")
      .map((word) => word.charAt(0)),
  );
  return initials.has(curp.charAt(0)) && initials.has(curp.charAt(3));
}

function fromCurpCertificate(fields: readonly LabeledField[], items: readonly PdfTextItem[]): ExtractedClientFields {
  // The labeled key first; otherwise any CURP in the text, preferring one whose check digit matches.
  const candidates = [
    validCurp(firstValue(fields, "key")),
    validCurp(firstValue(fields, "curp")),
    ...[...normalize(items.map((i) => i.text).join(" ")).matchAll(CURP_IN_TEXT)].map((m) => m[0]),
  ].filter((c): c is string => !!c && isValidCurp(c));
  const curp = candidates.find(curpCheckDigitMatches) ?? candidates[0];
  if (!curp) return {};
  const decoded = decodeCurp(curp);
  const name =
    firstValue(fields, "fullName") ??
    [firstValue(fields, "givenNames"), firstValue(fields, "firstSurname"), firstValue(fields, "secondSurname")]
      .filter(Boolean)
      .join(" ");
  return {
    type: "individual",
    curp,
    birthDate: decoded?.birthDate,
    name: name && nameMatchesCurp(name, curp) ? name : undefined,
    country: decoded && decoded.birthState !== "NE" ? "México" : undefined,
  };
}

const withoutEmpty = (fields: ExtractedClientFields): ExtractedClientFields =>
  Object.fromEntries(
    Object.entries(fields).filter(([, value]) => value !== undefined && value !== ""),
  ) as ExtractedClientFields;

/** Client data from the text of a PDF, if it is a CSF or a constancia de la CURP. */
export function extractClientFields(items: readonly PdfTextItem[], currentDate: IsoDate = today()): DocumentExtraction {
  const hasText = items.some((item) => item.text.trim() !== "");
  if (!hasText) return { source: null, fields: {}, hasText };
  const text = normalize(items.map((item) => item.text).join(" "));
  const fields = readLabeledFields(items);

  if (/CONSTANCIA DE SITUACION FISCAL|CEDULA DE IDENTIFICACION FISCAL/.test(text) && firstValue(fields, "rfc")) {
    return { source: "tax_certificate", fields: withoutEmpty(fromTaxCertificate(fields, currentDate)), hasText };
  }
  if (/CLAVE UNICA DE REGISTRO DE POBLACION|CONSTANCIA DE LA CURP/.test(text)) {
    const extracted = withoutEmpty(fromCurpCertificate(fields, items));
    if (extracted.curp) return { source: "curp_certificate", fields: extracted, hasText };
  }
  return { source: null, fields: {}, hasText };
}
